// src/hooks/useFishIQ.ts
// Frontend catalog/search hook for FishIQ species + regions.
// This hook does not call functions/src/fishiqGetSignals.ts directly.
// Nearby/shared-signal fetching is handled elsewhere in the app.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FishIQRegion, FishIQSpecies, FishIQTab } from '../types';
import {
  getSavedIds,
  listRegions,
  listSpecies,
  searchRegionsByName,
  searchSpeciesByName,
  toggleSavedRegion,
  toggleSavedSpecies,
  withWaterTypeBias,
  type WaterTypeBias,
} from '../services/fishIQ';

type UseFishIQOptions = {
  initialTab?: FishIQTab;
  initialQuery?: string;
  waterTypeBias?: WaterTypeBias;
};

export function useFishIQ(options: UseFishIQOptions = {}) {
  const [tab, setTab] = useState<FishIQTab>(options.initialTab || 'species');
  const [query, setQuery] = useState(options.initialQuery || '');
  const [waterTypeBias, setWaterTypeBias] = useState<WaterTypeBias>(options.waterTypeBias ?? null);

  // IMPORTANT: `species` is the full global FishIQ catalog.
  // Do not store water-biased results here, or Species Hunt/Shellfish+ collapses
  // to local freshwater matches only (for example: Crawfish on inland reads).
  const [species, setSpecies] = useState<FishIQSpecies[]>([]);
  const [regions, setRegions] = useState<FishIQRegion[]>([]);
  const [speciesResults, setSpeciesResults] = useState<FishIQSpecies[]>([]);
  const [regionResults, setRegionResults] = useState<FishIQRegion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [savedSpeciesIds, setSavedSpeciesIds] = useState<string[]>([]);
  const [savedRegionIds, setSavedRegionIds] = useState<string[]>([]);

  const bootedRef = useRef(false);

  useEffect(() => {
    setWaterTypeBias(options.waterTypeBias ?? null);
  }, [options.waterTypeBias]);

  useEffect(() => {
    const saved = getSavedIds();
    setSavedSpeciesIds(Array.isArray(saved.speciesIds) ? saved.speciesIds : []);
    setSavedRegionIds(Array.isArray(saved.regionIds) ? saved.regionIds : []);
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      setIsLoading(true);
      try {
        const [allSpecies, allRegions] = await Promise.all([listSpecies(), listRegions()]);
        if (!alive) return;

        setSpecies(allSpecies);
        setRegions(allRegions);
        setSpeciesResults(allSpecies);
        setRegionResults(allRegions);
        bootedRef.current = true;
      } catch (err) {
        console.error('[useFishIQ] initial load failed', err);
        if (!alive) return;
        setSpecies([]);
        setRegions([]);
        setSpeciesResults([]);
        setRegionResults([]);
      } finally {
        if (alive) setIsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!bootedRef.current) return;

    let alive = true;
    const trimmed = query.trim();

    (async () => {
      if (!trimmed) {
        setSpeciesResults(species);
        setRegionResults(regions);
        return;
      }

      setIsLoading(true);
      try {
        const [nextSpecies, nextRegions] = await Promise.all([
          // Species Hunt is a global catalog search. Keep water truth guarding in
          // Area Read callers via withWaterTypeBias/listSpeciesForWaterType, not here.
          searchSpeciesByName(trimmed),
          searchRegionsByName(trimmed),
        ]);

        if (!alive) return;
        setSpeciesResults(nextSpecies);
        setRegionResults(nextRegions);
      } catch (err) {
        console.error('[useFishIQ] search failed', err);
        if (!alive) return;
        setSpeciesResults([]);
        setRegionResults([]);
      } finally {
        if (alive) setIsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [query, species, regions]);

  const normalizeSavedKey = (value: any) =>
    String(value ?? '')
      .trim()
      .toLowerCase();

  const getSavedMatchKeys = (item: any): string[] => {
    const rawKeys = [
      item?.id,
      item?.slug,
      item?.key,
      item?.name,
      item?.label,
      item?.commonName,
    ];

    return Array.from(
      new Set(
        rawKeys
          .map(normalizeSavedKey)
          .filter(Boolean),
      ),
    );
  };

  const savedSpecies = useMemo(() => {
    const savedKeySet = new Set(savedSpeciesIds.map(normalizeSavedKey).filter(Boolean));

    return species.filter((item) =>
      getSavedMatchKeys(item).some((key) => savedKeySet.has(key)),
    );
  }, [species, savedSpeciesIds]);

  const savedRegions = useMemo(() => {
    const savedKeySet = new Set(savedRegionIds.map(normalizeSavedKey).filter(Boolean));

    return regions.filter((item) =>
      getSavedMatchKeys(item).some((key) => savedKeySet.has(key)),
    );
  }, [regions, savedRegionIds]);

  // Expose a guarded/ranked list for Area Read style consumers without poisoning
  // the global Species Hunt catalog.
  const waterRankedSpecies = useMemo(
    () => withWaterTypeBias(species, waterTypeBias),
    [species, waterTypeBias],
  );

  const visibleSpecies = useMemo(() => {
    if (tab === 'saved') return savedSpecies;
    if (tab === 'regions') return [];
    return speciesResults;
  }, [tab, speciesResults, savedSpecies]);

  const visibleRegions = useMemo(() => {
    if (tab === 'saved') return savedRegions;
    if (tab === 'regions') return regionResults;
    return [];
  }, [tab, regionResults, savedRegions]);

  const resolveToggleKey = (rawId: string, items: any[], savedIds: string[]) => {
    const incomingKey = normalizeSavedKey(rawId);
    const savedKeySet = new Set(savedIds.map(normalizeSavedKey).filter(Boolean));

    const match = items.find((item) =>
      getSavedMatchKeys(item).some((key) => key === incomingKey),
    );

    if (match) {
      const existingSavedKey = getSavedMatchKeys(match).find((key) => savedKeySet.has(key));
      if (existingSavedKey) {
        const originalSavedValue = savedIds.find((value) => normalizeSavedKey(value) === existingSavedKey);
        return originalSavedValue || existingSavedKey;
      }
    }

    return String(rawId);
  };

  const toggleSpeciesSavedById = (id: string) => {
    const toggleKey = resolveToggleKey(id, species, savedSpeciesIds);
    const next = toggleSavedSpecies(toggleKey);
    setSavedSpeciesIds(Array.isArray(next.speciesIds) ? next.speciesIds : []);
    setSavedRegionIds(Array.isArray(next.regionIds) ? next.regionIds : []);
  };

  const toggleRegionSavedById = (id: string) => {
    const toggleKey = resolveToggleKey(id, regions, savedRegionIds);
    const next = toggleSavedRegion(toggleKey);
    setSavedSpeciesIds(Array.isArray(next.speciesIds) ? next.speciesIds : []);
    setSavedRegionIds(Array.isArray(next.regionIds) ? next.regionIds : []);
  };

  return {
    tab,
    setTab,
    query,
    setQuery,
    isLoading,
    waterTypeBias,
    setWaterTypeBias,

    species: visibleSpecies,
    regions: visibleRegions,

    allSpecies: species,
    allRegions: regions,
    waterRankedSpecies,

    speciesResults,
    regionResults,

    savedSpecies,
    savedRegions,
    savedSpeciesIds,
    savedRegionIds,

    toggleSpeciesSaved: toggleSpeciesSavedById,
    toggleRegionSaved: toggleRegionSavedById,
  };
}
